"use client";

import { useMemo } from "react";
import { useLocale, useTranslations } from "next-intl";
import { clsx } from "clsx";

import { defaultLocale } from "@mw/shared/i18n";
import { TabList } from "../core/TabList";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../core/Select";
import { CurrencySelector } from "../navigation/CurrencySelector";
import { BillingCycleToggle } from "./BillingCycleToggle";
import { usePricingTableContext } from "./PricingTableContext";

export type PlatformVariant = "all" | "creative_platform" | "agents_platform" | "api";
export type BillingCycle = "monthly" | "yearly";
export type PlanType = "individual" | "business";

const LOCALES_WITH_CURRENCY_SELECTOR = ["hi"];

interface PricingControlsV3Props {
  showPlanTypeSelector?: boolean;
  showPlatformSelector?: boolean;
  /** Filter which platform tabs to show. If not provided, all tabs are shown. */
  platformOptions?: PlatformVariant[];
}

export const PricingControlsV3 = ({
  showPlanTypeSelector = true,
  showPlatformSelector = true,
  platformOptions,
}: PricingControlsV3Props) => {
  const t = useTranslations("PricingClientKeys.PricingCard");
  const locale = useLocale();
  const { selectedPlatform, billingCycle, planType, setPlanType } = usePricingTableContext();

  const localePath = locale === defaultLocale ? "" : `/${locale}`;

  const suffix = useMemo(() => {
    const params = new URLSearchParams();
    if (billingCycle !== "monthly") params.set("price.billing_cycle", billingCycle);
    if (planType !== "individual") params.set("price.plan_type", planType);
    return params.size > 0 ? `?${params}` : "";
  }, [billingCycle, planType]);

  const allPlatformTabs = [
    {
      id: "creative_platform",
      title: t("platform_tabs.creative_platform"),
      href: `${localePath}/pricing${suffix}`,
      isSelected: selectedPlatform === "creative_platform",
    },
    {
      id: "agents_platform",
      title: t("platform_tabs.agents_platform"),
      href: `${localePath}/pricing/agents${suffix}`,
      isSelected: selectedPlatform === "agents_platform",
    },
    {
      id: "api",
      title: t("platform_tabs.api"),
      href: `${localePath}/pricing/api${suffix}`,
      isSelected: selectedPlatform === "api",
    },
  ];

  const platformTabs = platformOptions
    ? allPlatformTabs.filter(tab => platformOptions.includes(tab.id as PlatformVariant))
    : allPlatformTabs;

  return (
    <div className='tw-mb-8 tw-flex tw-flex-col tw-gap-3 md:tw-flex-row md:tw-flex-wrap md:tw-items-center md:tw-justify-between'>
      {/* Platform Tabs */}
      {showPlatformSelector && (
        <div className='tw-flex-1'>
          <TabList items={platformTabs} showRing={true} size='lg' />
        </div>
      )}

      {/* Right Side Controls - use ml-auto when platform selector is hidden to keep controls on right */}
      <div className={clsx("tw-flex tw-gap-3", !showPlatformSelector && "md:tw-ml-auto")}>
        {/* Currency Selector - Only show for Hindi locale */}
        {LOCALES_WITH_CURRENCY_SELECTOR.includes(locale) && (
          <CurrencySelector variant='outline-new' size='lg' overlayStyle />
        )}

        {/* Billing Cycle Select */}
        {selectedPlatform === "creative_platform" && <BillingCycleToggle />}

        {/* Individual/Business Plans Select - Hidden on mobile */}
        {showPlanTypeSelector && (
          <Select value={planType} onValueChange={value => setPlanType(value as PlanType)}>
            <SelectTrigger
              variant='outline-new'
              size='lg'
              className='tw-min-w-[160px] tw-hidden md:tw-flex'
              overlayStyle
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='individual' fontVariant='new'>
                {t("plan_type.individual")}
              </SelectItem>
              <SelectItem value='business' fontVariant='new'>
                {t("plan_type.business")}
              </SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>
    </div>
  );
};
